// ─── Network Manager (Colyseus Client) — With Reconnection ─────────────────

import { Client, Room } from "colyseus.js";

const SERVER_URL =
  (import.meta as any).env?.VITE_SERVER_URL ||
  (() => {
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const host = window.location.hostname;
    // In production behind nginx (port 443/80), don't append port
    const needsPort = window.location.port && !["443", "80", ""].includes(window.location.port);
    return needsPort ? `${proto}://${host}:2567` : `${proto}://${host}`;
  })();

const STORAGE_KEY = "moor_session";

interface SavedSession {
  roomId: string;
  reconnectToken: string;
  displayName: string;
  timestamp: number;
}

function saveSession(data: SavedSession) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function loadSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as SavedSession;
    // Expire after 10 minutes
    if (Date.now() - data.timestamp > 10 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch { return null; }
}

function clearSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

export class NetworkManager {
  private client: Client;
  private room: Room | null = null;

  public sessionId: string = "";
  public seatIndex: number = -1;
  public reconnectToken: string = "";
  public isAdmin: boolean = false;

  // Event callbacks
  public onStateUpdate: ((state: any) => void) | null = null;
  public onGameEvent: ((data: any) => void) | null = null;
  public onError: ((data: any) => void) | null = null;
  public onJoined: ((data: any) => void) | null = null;
  public onDisconnect: ((code: number) => void) | null = null;

  constructor() {
    this.client = new Client(SERVER_URL);
  }

  async createRoom(displayName: string): Promise<string> {
    try {
      const authToken = this.getAuthToken();
      this.room = await this.client.create("moor", { displayName, authToken });
      this.setupListeners(displayName);
      return this.room.id;
    } catch (err: any) {
      console.error("Failed to create room:", err);
      throw err;
    }
  }

  async createRoom3p(displayName: string): Promise<string> {
    try {
      const authToken = this.getAuthToken();
      this.room = await this.client.create("moor3p", { displayName, authToken });
      this.setupListeners(displayName);
      return this.room.id;
    } catch (err: any) {
      console.error("Failed to create 3p room:", err);
      throw err;
    }
  }

  async joinRoom(roomId: string, displayName: string): Promise<void> {
    try {
      const authToken = this.getAuthToken();
      this.room = await this.client.joinById(roomId, { displayName, authToken });
      this.setupListeners(displayName);
    } catch (err: any) {
      console.error("Failed to join room:", err);
      throw err;
    }
  }

  async tryReconnect(): Promise<boolean> {
    const saved = loadSession();
    if (!saved) return false;

    try {
      console.log(`[Network] Attempting reconnect to room ${saved.roomId}...`);
      const authToken = this.getAuthToken();
      this.room = await this.client.joinById(saved.roomId, {
        displayName: saved.displayName,
        reconnectToken: saved.reconnectToken,
        authToken,
      });
      this.setupListeners(saved.displayName);
      console.log("[Network] Reconnection successful!");
      return true;
    } catch (err) {
      console.log("[Network] Reconnection failed:", err);
      clearSession();
      return false;
    }
  }

  getSavedSession(): SavedSession | null {
    return loadSession();
  }

  clearSavedSession() {
    clearSession();
  }

  private setupListeners(displayName?: string) {
    if (!this.room) return;

    this.room.onMessage("joined", (data) => {
      this.sessionId = data.sessionId;
      this.seatIndex = data.seatIndex;
      this.reconnectToken = data.reconnectToken || "";
      this.isAdmin = data.isAdmin || false;

      // Save session for reconnection
      if (this.room && this.reconnectToken) {
        saveSession({
          roomId: this.room.id,
          reconnectToken: this.reconnectToken,
          displayName: displayName || "لاعب",
          timestamp: Date.now(),
        });
      }

      if (this.onJoined) this.onJoined(data);
    });

    this.room.onMessage("state", (state) => {
      // Refresh saved session timestamp on each state update
      const saved = loadSession();
      if (saved) {
        saved.timestamp = Date.now();
        saveSession(saved);
      }
      if (this.onStateUpdate) this.onStateUpdate(state);
    });

    this.room.onMessage("game-event", (data) => {
      if (this.onGameEvent) this.onGameEvent(data);
    });

    this.room.onMessage("error", (data) => {
      if (this.onError) this.onError(data);
    });

    this.room.onMessage("action-error", (data) => {
      if (this.onError) this.onError(data);
    });

    this.room.onMessage("chat", (data) => {
      if (this.onChat) this.onChat(data);
    });

    this.room.onMessage("left", (data) => {
      if (this.onLeft) this.onLeft(data);
    });

    this.room.onMessage("kicked", (data) => {
      if (this.onKicked) this.onKicked(data);
    });

    this.room.onLeave((code) => {
      console.log("Left room, code:", code);
      // code 1000 = normal close, 4000+ = custom
      if (code !== 1000) {
        // Abnormal disconnect — keep session for reconnection
        if (this.onDisconnect) this.onDisconnect(code);
      } else {
        clearSession();
      }
    });
  }

  sendAction(action: any) {
    if (!this.room) return;
    this.room.send("action", action);
  }

  sendStartGame() {
    if (!this.room) return;
    this.room.send("start-game", {});
  }

  sendNewRound() {
    if (!this.room) return;
    this.room.send("new-round", {});
  }

  sendRestartGame() {
    if (!this.room) return;
    this.room.send("restart-game", {});
  }

  sendAdminReorder(seat1: number, seat2: number) {
    if (!this.room) return;
    this.room.send("admin-reorder", { seat1, seat2 });
  }

  sendAdminKick(seatIndex: number) {
    if (!this.room) return;
    this.room.send("admin-kick", { seatIndex });
  }

  sendAddBot() {
    if (!this.room) return;
    this.room.send("admin-add-bot", {});
  }

  sendRemoveBot(seatIndex: number) {
    if (!this.room) return;
    this.room.send("admin-remove-bot", { seatIndex });
  }

  sendChat(text: string) {
    if (!this.room) return;
    this.room.send("chat", { text });
  }

  leaveRoom() {
    if (this.room) {
      this.room.leave(true); // consented leave
      this.room = null;
    }
    clearSession();
    this.sessionId = "";
    this.seatIndex = -1;
    this.reconnectToken = "";
    this.isAdmin = false;
  }

  sendLeaveGame() {
    if (!this.room) return;
    this.room.send("leave-game", {});
  }

  async joinRoomAsSpectator(code: string, displayName: string): Promise<string> {
    const httpProto = window.location.protocol;
    const host = window.location.hostname;
    const needsPort = window.location.port && !["443", "80", ""].includes(window.location.port);
    const baseUrl = needsPort ? `${httpProto}//${host}:2567` : `${httpProto}//${host}`;
    
    const resp = await fetch(`${baseUrl}/api/room-by-code/${code.toUpperCase()}`);
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || "لم يتم العثور على غرفة بهذا الكود");
    }
    const { roomId } = await resp.json();
    const authToken = this.getAuthToken();
    this.room = await this.client.joinById(roomId, { displayName, authToken, spectator: true });
    this.setupListeners(displayName);
    return roomId;
  }

  public onChat: ((data: any) => void) | null = null;
  public onLeft: ((data: any) => void) | null = null;
  public onKicked: ((data: any) => void) | null = null;

  async joinRoomByCode(code: string, displayName: string): Promise<string> {
    // Look up room ID via the code API
    const httpProto = window.location.protocol;
    const host = window.location.hostname;
    const needsPort = window.location.port && !["443", "80", ""].includes(window.location.port);
    const baseUrl = needsPort ? `${httpProto}//${host}:2567` : `${httpProto}//${host}`;
    
    const resp = await fetch(`${baseUrl}/api/room-by-code/${code.toUpperCase()}`);
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || "لم يتم العثور على غرفة بهذا الكود");
    }
    const { roomId } = await resp.json();
    await this.joinRoom(roomId, displayName);
    return roomId;
  }

  getRoomId(): string {
    return this.room?.id || "";
  }

  getSessionId(): string {
    return this.room?.sessionId || "";
  }

  isConnected(): boolean {
    return this.room !== null;
  }

  private getAuthToken(): string | undefined {
    try {
      return localStorage.getItem("moor_auth_token") || undefined;
    } catch {
      return undefined;
    }
  }
}

export const network = new NetworkManager();
